package com.playoffpulse.frontend.state

import com.playoffpulse.frontend.data.PlayoffDataProvider
import com.playoffpulse.frontend.ui.ShareFormatter
import com.playoffpulse.shared.model.BestCaseClinchResponse
import com.playoffpulse.shared.model.BlockedShotsChartBundleResponse
import com.playoffpulse.shared.model.BlockedShotsMissesMode
import com.playoffpulse.shared.model.BlockedShotsChartResponse
import com.playoffpulse.shared.model.BlockedShotsTeamDetailResponse
import com.playoffpulse.shared.model.DraftWatchResponse
import com.playoffpulse.shared.model.ImportantGamesResponse
import com.playoffpulse.shared.model.ImportantGamesObjective
import com.playoffpulse.shared.model.ImportantGamesWindow
import com.playoffpulse.shared.model.LeagueId
import com.playoffpulse.shared.model.MagicSummary
import com.playoffpulse.shared.model.SnapshotMeta
import com.playoffpulse.shared.model.StandingsInsightsResponse
import com.playoffpulse.shared.model.StandingsSnapshot
import com.playoffpulse.shared.model.TeamRef
import com.playoffpulse.shared.model.TeamStanding
import com.playoffpulse.shared.model.TopRivalsResponse
import com.playoffpulse.shared.model.VanquishedResponse
import com.playoffpulse.shared.model.YesterdayMovementResponse
import kotlinx.coroutines.CancellationException
import kotlinx.datetime.Clock
import kotlinx.datetime.Instant

class PlayoffPulseController(
    private val provider: PlayoffDataProvider,
    private val nowProvider: () -> Instant = { Clock.System.now() },
) {
    private var cachedSnapshot: StandingsSnapshot? = null

    suspend fun bootstrap(preferredTeamId: String? = null): AppUiState {
        val teams = provider.getTeams(LeagueId.NHL)
        if (teams.isEmpty()) {
            return AppUiState(isLoading = false, errorMessage = "No teams available right now.")
        }
        val fallbackTeamId = teams.first().id

        return try {
            val snapshot = provider.getStandings(LeagueId.NHL)
            val standingsInsights = runCatching { provider.getStandingsInsights(LeagueId.NHL) }.getOrNull()
            cachedSnapshot = snapshot
            val preferred = preferredTeamId
                ?.uppercase()
                ?.takeIf { requestedTeamId -> teams.any { it.id.equals(requestedTeamId, ignoreCase = true) } }
            val baseState = AppUiState(
                teams = teams,
                selectedTeamId = preferred,
                standings = snapshot.standings,
                standingsInsights = standingsInsights,
                snapshotMeta = snapshot.meta,
                isLoading = false,
                isRefreshing = false,
                isStale = isSnapshotStale(snapshot.meta),
                errorMessage = null,
            )
            if (preferred == null) {
                baseState
            } else {
                loadTeamData(baseState, preferred)
            }
        } catch (ex: Throwable) {
            val selectedTeamId = preferredTeamId
                ?.uppercase()
                ?.takeIf { requestedTeamId -> teams.any { it.id.equals(requestedTeamId, ignoreCase = true) } }
            AppUiState(
                teams = teams,
                selectedTeamId = selectedTeamId,
                isLoading = false,
                errorMessage = "Could not load standings: ${ex.message.orEmpty()}",
            )
        }
    }

    suspend fun selectTeam(current: AppUiState, teamId: String): AppUiState {
        if (
            current.selectedTeamId == teamId &&
            current.summary?.team?.id?.equals(teamId, ignoreCase = true) == true
        ) {
            return current
        }

        val snapshot = cachedSnapshot ?: run {
            return try {
                val fresh = provider.getStandings(LeagueId.NHL)
                cachedSnapshot = fresh
                val base = buildStateFromSnapshot(current, teamId, fresh, null).copy(
                    summary = null,
                    bestCaseClinch = null,
                    topRivals = null,
                    vanquished = null,
                    draftWatch = null,
                    importantGamesToday = null,
                    importantGamesWeek = null,
                    importantGamesRemaining = null,
                )
                loadTeamData(base, teamId)
            } catch (ex: Throwable) {
                current.copy(
                    selectedTeamId = teamId,
                    errorMessage = "Could not load a summary for $teamId: ${ex.message.orEmpty()}",
                )
            }
        }

        val base = buildStateFromSnapshot(current, teamId, snapshot, null).copy(
            summary = null,
            bestCaseClinch = null,
            topRivals = null,
            vanquished = null,
            draftWatch = null,
            importantGamesToday = null,
            importantGamesWeek = null,
            importantGamesRemaining = null,
        )
        return loadTeamData(base, teamId)
    }

    suspend fun refresh(current: AppUiState): AppUiState {
        if (current.selectedTeamId == null) return current

        return try {
            val fresh = provider.getStandings(LeagueId.NHL)
            val standingsInsights = runCatching { provider.getStandingsInsights(LeagueId.NHL) }.getOrNull()
            cachedSnapshot = fresh
            val base = buildStateFromSnapshot(current, current.selectedTeamId, fresh, null).copy(
                standingsInsights = standingsInsights,
                blockedShotsChartBundles = emptyMap(),
                blockedShotsChart = null,
                blockedShotsTeamDetails = emptyMap(),
                blockedShotsTeamDetailLoadingTeamIds = emptySet(),
                blockedShotsTeamDetailErrors = emptyMap(),
            )
            loadYesterdayMovement(loadTeamData(base, current.selectedTeamId))
        } catch (ex: Throwable) {
            val fallback = cachedSnapshot
            if (fallback != null) {
                buildStateFromSnapshot(
                    current = current,
                    teamId = current.selectedTeamId,
                    snapshot = fallback,
                    errorMessage = "Live refresh failed. Showing latest cached snapshot.",
                    forceStale = true,
                )
            } else {
                current.copy(errorMessage = "Refresh failed: ${ex.message.orEmpty()}")
            }
        }
    }

    suspend fun loadYesterdayMovement(current: AppUiState): AppUiState {
        return try {
            current.copy(
                yesterdayMovement = provider.getYesterdayMovement(LeagueId.NHL),
            )
        } catch (_: Throwable) {
            current.copy(
                yesterdayMovement = YesterdayMovementResponse(
                    league = LeagueId.NHL,
                    available = false,
                    latestSnapshotGeneratedAtIso = current.snapshotMeta?.generatedAtIso,
                    comparedSnapshotDateEt = null,
                    movement = emptyList(),
                ),
            )
        }
    }

    suspend fun loadBlockedShotsChart(
        current: AppUiState,
        missesMode: BlockedShotsMissesMode,
    ): AppUiState {
        return try {
            val bundle = current.blockedShotsChartBundles[missesMode]
                ?: provider.getBlockedShotsChartBundle(LeagueId.NHL, missesMode)
            current.copy(
                blockedShotsChartBundles = current.blockedShotsChartBundles + (missesMode to bundle),
                blockedShotsChart = bundle.allChart,
                blockedShotsTeamDetails = emptyMap(),
                blockedShotsTeamDetailLoadingTeamIds = emptySet(),
                blockedShotsTeamDetailErrors = emptyMap(),
                blockedShotsChartLoading = false,
                blockedShotsChartError = null,
            )
        } catch (ex: CancellationException) {
            throw ex
        } catch (ex: Throwable) {
            current.copy(
                blockedShotsChartLoading = false,
                blockedShotsChartError = "Could not load charts: ${ex.message.orEmpty()}",
            )
        }
    }

    suspend fun loadBlockedShotsTeamDetail(
        current: AppUiState,
        teamId: String,
        missesMode: BlockedShotsMissesMode,
    ): AppUiState {
        val normalizedTeamId = teamId.uppercase()
        return try {
            current.copy(
                blockedShotsTeamDetails = current.blockedShotsTeamDetails + (
                    normalizedTeamId to provider.getBlockedShotsTeamDetail(
                        teamId = normalizedTeamId,
                        league = LeagueId.NHL,
                        missesMode = missesMode,
                    )
                ),
                blockedShotsTeamDetailLoadingTeamIds = current.blockedShotsTeamDetailLoadingTeamIds - normalizedTeamId,
                blockedShotsTeamDetailErrors = current.blockedShotsTeamDetailErrors - normalizedTeamId,
            )
        } catch (ex: CancellationException) {
            throw ex
        } catch (ex: Throwable) {
            current.copy(
                blockedShotsTeamDetailLoadingTeamIds = current.blockedShotsTeamDetailLoadingTeamIds - normalizedTeamId,
                blockedShotsTeamDetailErrors = current.blockedShotsTeamDetailErrors + (
                    normalizedTeamId to "Could not load team games: ${ex.message.orEmpty()}"
                ),
            )
        }
    }

    fun shareText(current: AppUiState): String? = current.summary?.let { ShareFormatter.shareText(it, current.draftWatch) }

    fun isSnapshotStale(meta: SnapshotMeta): Boolean {
        val generatedAt = try {
            Instant.parse(meta.generatedAtIso)
        } catch (_: Throwable) {
            return false
        }

        val diffMinutes = (nowProvider().toEpochMilliseconds() - generatedAt.toEpochMilliseconds()) / 60_000
        return diffMinutes > meta.staleAfterMinutes
    }

    private fun buildStateFromSnapshot(
        current: AppUiState,
        teamId: String,
        snapshot: StandingsSnapshot,
        errorMessage: String?,
        forceStale: Boolean = false,
    ): AppUiState {
        return current.copy(
            selectedTeamId = teamId,
            standings = snapshot.standings,
            snapshotMeta = snapshot.meta,
            isLoading = false,
            isRefreshing = false,
            isStale = forceStale || isSnapshotStale(snapshot.meta),
            errorMessage = errorMessage,
        )
    }

    private suspend fun loadTeamData(
        current: AppUiState,
        teamId: String,
    ): AppUiState {
        return try {
            val summary = provider.getMagicSummary(teamId, LeagueId.NHL)
            val bestCaseClinch = runCatching {
                provider.getBestCaseClinch(teamId, LeagueId.NHL)
            }.getOrNull()
            val topRivals = provider.getTopRivals(teamId, LeagueId.NHL)
            val vanquished = runCatching {
                provider.getVanquished(teamId, LeagueId.NHL)
            }.getOrNull()
            val draftWatch = runCatching {
                provider.getDraftWatch(teamId, LeagueId.NHL)
            }.getOrNull()
            val importantObjective = if (draftWatch?.active == true) {
                ImportantGamesObjective.DRAFT
            } else {
                ImportantGamesObjective.PLAYOFF
            }
            val today = provider.getImportantGames(teamId, ImportantGamesWindow.TODAY, importantObjective, LeagueId.NHL)
            val week = provider.getImportantGames(teamId, ImportantGamesWindow.WEEK, importantObjective, LeagueId.NHL)
            val remaining = if (draftWatch?.active == true || bestCaseClinch?.scenarioAvailable == true) {
                runCatching {
                    provider.getImportantGames(teamId, ImportantGamesWindow.REMAINING, importantObjective, LeagueId.NHL)
                }.getOrNull()
            } else {
                null
            }
            current.copy(
                summary = summary,
                bestCaseClinch = bestCaseClinch,
                topRivals = topRivals,
                vanquished = vanquished,
                draftWatch = draftWatch,
                importantGamesToday = today,
                importantGamesWeek = week,
                importantGamesRemaining = remaining,
            )
        } catch (ex: Throwable) {
            current.copy(
                errorMessage = current.errorMessage ?: "Team data unavailable: ${ex.message.orEmpty()}",
            )
        }
    }

}

data class AppUiState(
    val teams: List<TeamRef> = emptyList(),
    val selectedTeamId: String? = null,
    val summary: MagicSummary? = null,
    val bestCaseClinch: BestCaseClinchResponse? = null,
    val standings: List<TeamStanding> = emptyList(),
    val standingsInsights: StandingsInsightsResponse? = null,
    val snapshotMeta: SnapshotMeta? = null,
    val yesterdayMovement: YesterdayMovementResponse? = null,
    val topRivals: TopRivalsResponse? = null,
    val vanquished: VanquishedResponse? = null,
    val draftWatch: DraftWatchResponse? = null,
    val blockedShotsChartBundles: Map<BlockedShotsMissesMode, BlockedShotsChartBundleResponse> = emptyMap(),
    val blockedShotsChart: BlockedShotsChartResponse? = null,
    val blockedShotsTeamDetails: Map<String, BlockedShotsTeamDetailResponse> = emptyMap(),
    val importantGamesToday: ImportantGamesResponse? = null,
    val importantGamesWeek: ImportantGamesResponse? = null,
    val importantGamesRemaining: ImportantGamesResponse? = null,
    val blockedShotsChartLoading: Boolean = false,
    val blockedShotsChartError: String? = null,
    val blockedShotsTeamDetailLoadingTeamIds: Set<String> = emptySet(),
    val blockedShotsTeamDetailErrors: Map<String, String> = emptyMap(),
    val isLoading: Boolean = true,
    val isRefreshing: Boolean = false,
    val isStale: Boolean = false,
    val errorMessage: String? = null,
)
